// ============================================================================
// apps/web/src/components/KioskRail.tsx
// ----------------------------------------------------------------------------
// Rail de navigation PERMANENT pour les sessions kiosque (tablette du magasin).
// La barre latérale « au survol » de l'AppShell est inutilisable au tactile
// (pas de hover) : le kiosque a donc un rail gauche TOUJOURS déployé, présent
// aussi bien sur les modules (dans l'AppShell) que sur la page d'accueil PV
// (`LocationDashboardPage`, hors AppShell).
//
// Les modules affichés sont CONFIGURABLES depuis Réglages → Kiosque
// (`loadKioskModules`). « Accueil PV » est toujours présent. Confinement
// inchangé : aucun accès aux autres PV / Réglages.
//
// Largeur = 4.75rem. Le contenu appelant doit ajouter `sm:pl-[4.75rem]`.
// ============================================================================

import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { LayoutGrid } from 'lucide-react';
import { cn } from '@/lib/utils';
import { KIOSK_MODULE_CATALOG, loadKioskModules } from '@/lib/kiosk';
import { useNewFeedbackCount } from '@/lib/use-feedback-count';
import { useNewCommandesCount } from '@/lib/use-commandes-count';
import { usePendingAbsencesCount } from '@/lib/use-pending-absences-count';

const ITEM =
  'relative flex flex-col items-center justify-center gap-1 rounded-lg px-1 py-3 text-[10px] font-medium leading-tight text-center transition-colors';

// Pastille de compteur en coin de tuile (retours à traiter, commandes, demandes).
function RailBadge({ n }: { n: number }) {
  if (n <= 0) return null;
  return (
    <span className="absolute right-1 top-1 min-w-[16px] rounded-full bg-rose-600 px-1 text-center text-[9px] font-bold leading-[16px] text-white">
      {n > 99 ? '99+' : n}
    </span>
  );
}

export function KioskRail({ slug }: { slug: string | null }) {
  const [enabled, setEnabled] = useState<string[] | null>(null);
  useEffect(() => { void loadKioskModules(slug).then(setEnabled); }, [slug]);

  // Compteurs (badges) des tuiles kiosque : retours produits, commandes, demandes planning.
  const feedbackCount = useNewFeedbackCount();
  const commandesCount = useNewCommandesCount();
  const absencesCount = usePendingAbsencesCount();
  const badgeFor = (key: string): number =>
    key === 'feedback' ? feedbackCount
      : key === 'production' ? commandesCount
      : key === 'planning' ? absencesCount
      : 0;

  const home = slug ? `/pv/${slug}` : '/';
  const navCls = ({ isActive }: { isActive: boolean }) =>
    cn(ITEM, isActive ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-accent');

  // Modules activés, dans l'ordre du catalogue.
  const mods = KIOSK_MODULE_CATALOG.filter((m) => (enabled ?? []).includes(m.key));

  return (
    <aside
      className="fixed left-0 top-0 z-50 hidden h-screen w-[4.75rem] flex-col gap-1 overflow-y-auto
                 border-r bg-background px-1.5 py-3 shadow-sm sm:flex"
      aria-label="Navigation kiosque"
    >
      {/* Accueil = tableau de bord du PV, qui regroupe l'accès à tous les modules.
          Icône « grille de modules » (au lieu d'une maison) pour que le staff
          comprenne que c'est le point d'entrée vers les autres modules. */}
      <NavLink to={home} end className={navCls} title="Accueil — accès à tous les modules">
        <LayoutGrid className="h-5 w-5" />
        Accueil
      </NavLink>
      {mods.map((m) => {
        const Icon = m.icon;
        const n = badgeFor(m.key);
        if (m.href) {
          return (
            <a key={m.key} href={m.href} target="_blank" rel="noreferrer"
              className={cn(ITEM, 'text-foreground hover:bg-accent')}>
              <RailBadge n={n} />
              <Icon className="h-5 w-5" />
              {m.label}
            </a>
          );
        }
        return (
          <NavLink key={m.key} to={m.to!(slug)} className={navCls}>
            <RailBadge n={n} />
            <Icon className="h-5 w-5" />
            {m.label}
          </NavLink>
        );
      })}
    </aside>
  );
}
